import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import {
  inViewOnceStagger,
  inViewFirstHomeWork,
  staggerContainer,
  itemFade,
} from '../motionReveal';
import SideQuests from './SideQuests';

const Section = styled.section`
  padding: 5rem 2rem 120px;
  background: var(--bg);
  scroll-margin-top: 5.5rem;

  @media (max-width: 640px) {
    padding: 3rem 1.25rem 4rem;
  }
`;

const Container = styled.div`
  max-width: 1420px;
  margin: 0 auto;
`;

const WorksGutter = styled.div`
  margin-left: 140px;
  margin-right: 140px;

  @media (max-width: 960px) {
    margin-left: 24px;
    margin-right: 24px;
  }

  @media (max-width: 640px) {
    margin-left: 0;
    margin-right: 0;
  }
`;

const StaggerWrap = styled(motion.div)``;

const WorkItem = styled(motion.article)`
  margin-bottom: 160px;

  @media (hover: hover) and (pointer: fine) {
    &:hover .work-image-stage {
      transform: translateY(-4px);
      box-shadow: 0 16px 40px rgba(28, 28, 26, 0.08);
    }
  }

  @media (max-width: 640px) {
    margin-bottom: 112px;
  }
`;

const WorkHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;
  margin-bottom: 1.5rem;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.85rem;
  }
`;

const BrandLockup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.85rem;
  min-width: 0;
`;

const LogoFrame = styled.div`
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  border: 1px solid var(--border);
  background: var(--bg);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const CompanyLogo = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const BrandText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
`;

const CompanyName = styled.h3`
  margin: 0;
  font-family: var(--font-serif);
  font-size: 24px;
  font-weight: 500;
  letter-spacing: -0.02em;
  color: var(--text);
  line-height: 1.25;
`;

const Tagline = styled.p`
  margin: 0;
  font-family: var(--font-sans);
  font-size: 18px;
  font-weight: 400;
  color: var(--text-muted);
  line-height: 1.35;
`;

const MetaRight = styled.p`
  margin: 0;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  font-family: var(--font-sans);
  font-size: 18px;
  font-weight: 400;
  color: var(--text-muted);
  line-height: 1.35;
  text-align: right;
  white-space: nowrap;

  @media (max-width: 640px) {
    align-items: flex-start;
    text-align: left;
    white-space: normal;
    padding-left: calc(48px + 0.85rem);
  }
`;

const ImageStage = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 10;
  border-radius: 12px;
  overflow: hidden;
  background: var(--surface);
  border: 1px solid var(--border);
  padding: 32px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.4s ease, box-shadow 0.4s ease;

  @media (max-width: 640px) {
    padding: 16px;
  }
`;

const MockupFrame = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 8px;
  overflow: hidden;
  background: var(--bg);
`;

const ScreenImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;
  pointer-events: none;
`;

const WorkSummary = styled.p`
  margin: 1.25rem 0 0;
  width: 100%;
  max-width: none;
  font-family: var(--font-sans);
  font-size: 20px;
  font-weight: 400;
  color: var(--text-muted);
  line-height: 1.55;
  letter-spacing: -0.01em;
`;

const SelectedWorks = () => {
  const selectedWorks = [
    {
      id: 1,
      companyName: 'Wayfair',
      companyLogoSrc: '/logos/wayfair.svg',
      tagline: 'Built AI products for Wayfair business suppliers',
      role: 'Product Designer',
      year: '2025 – 2026',
      summary:
        "I was the sole designer for Partner Assistant, an AI virtual assistant that helps Wayfair suppliers get contextual answers about their business. This launched to 11k+ businesses and became the foundation for AI standards across the Partner Home platform.",
      image: '/wayfair-ai-assistant.png',
      caseStudyUrl: '/case-studies/wayfair-ai-assistant',
      screenImageClass: 'screen-image--wayfair-ai-assistant',
      mockup: {
        idleBg: '#eceff4',
        hoverBg: '#FFEDFF',
      },
    },
    {
      id: 2,
      companyName: 'Freebites',
      companyLogoSrc: '/logos/freebites.svg',
      tagline: 'Created a food sharing app for thousands of students',
      role: 'Cofounder, Founding Product Designer',
      year: '2023 – 2026',
      summary:
        'I cofounded and designed Freebites, an award-winning 501(c)(3) nonprofit mobile app that socializes free food on college campuses. As of January 2026, Freebites has 6k+ downloads, $10k+ in funding, and a 20-person team.',
      image: '/freebites.png',
      caseStudyUrl: '/case-studies/freebites',
      screenImageClass: 'screen-image--freebites',
      mockup: {
        idleBg: '#eceff4',
        hoverBg: '#FFEEDD',
      },
    },
    {
      id: 3,
      companyName: 'Wayfair Professional',
      companyLogoSrc: '/logos/wayfair_professional.svg',
      tagline: 'Designed a project management tool for Wayfair business customers',
      role: 'Product Design Intern',
      year: '2024',
      summary:
        'As an intern on the Wayfair Professional team, I helped design the Calendar experience for My Projects, a new tool for tracking orders, timelines, and dependencies across large-scale projects. My Projects platform launched to 6.7k+ businesses.',
      image: '/wayfair-professional.png',
      caseStudyUrl: 'https://www.wayfair.com/m/pro-myprojects-howto',
      screenImageClass: 'screen-image--wayfair-professional',
      imageStageClass: 'image-stage--wayfair-professional',
      mockup: {
        idleBg: '#eceff4',
        hoverBg: '#F0E7FE',
      },
    },
  ];

  const [firstWork, ...restWorks] = selectedWorks;

  const renderWorkCard = (work, motionProps = {}) => (
    <WorkItem
      key={work.id}
      variants={itemFade}
      {...motionProps}
      style={{
        '--stage-bg-idle': work.mockup.idleBg,
      }}
    >
      <WorkHeader>
        <BrandLockup>
          <LogoFrame>
            <CompanyLogo src={work.companyLogoSrc} alt="" />
          </LogoFrame>
          <BrandText>
            <CompanyName>{work.companyName}</CompanyName>
            <Tagline>{work.tagline}</Tagline>
          </BrandText>
        </BrandLockup>
        <MetaRight>
          <span>{work.role}</span>
          <span>{work.year}</span>
        </MetaRight>
      </WorkHeader>

      <ImageStage
        className={['work-image-stage', work.imageStageClass]
          .filter(Boolean)
          .join(' ')}
      >
        <MockupFrame className={work.screenImageClass}>
          <ScreenImage src={work.image} alt="" />
        </MockupFrame>
      </ImageStage>

      <WorkSummary>{work.summary}</WorkSummary>
    </WorkItem>
  );

  return (
    <Section id="selected-work">
      <Container>
        <WorksGutter>
          {renderWorkCard(firstWork, {
            initial: 'hidden',
            whileInView: 'visible',
            viewport: inViewFirstHomeWork,
            variants: itemFade,
          })}
          <StaggerWrap
            initial="hidden"
            whileInView="visible"
            viewport={inViewOnceStagger}
            variants={staggerContainer}
          >
            {restWorks.map((work) => renderWorkCard(work))}
          </StaggerWrap>
          <SideQuests />
        </WorksGutter>
      </Container>
    </Section>
  );
};

export default SelectedWorks;
